import React, { useEffect, useState, useContext } from "react";
import { db, storage } from "./firebase/firebaseConfig";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { UserContext } from "./context/UserContext";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const Wrapper = styled.div`
  padding: 40px;
  max-width: 900px;
  margin: auto;
  color: #fff;
`;

const Heading = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #00d4ff;
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #00d4ff;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const EditorWrapper = styled.div`
  margin-bottom: 20px;
  background: #fff;
  border-radius: 8px;
  color: #000;
  padding: 10px;
`;

const Button = styled.button`
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  color: #121212;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: linear-gradient(135deg, #00bcd4, #0097a7);
  }
`;

const ThumbnailPreview = styled.img`
  width: 200px;
  border-radius: 8px;
  margin-bottom: 15px;
  display: block;
`;

const VideoPreview = styled.video`
  width: 100%;
  max-height: 250px;
  border-radius: 8px;
  margin-bottom: 15px;
  display: block;
`;

const technologyCategories = [
  "React",
  "React Native",
  "Angular",
  "Vue.js",
  "Next.js",
  "Node.js",
  "Express.js",
  "Django",
  "Flask",
  "Spring Boot",
  "Laravel",
  "Ruby on Rails",
  "Python",
  "Java",
  "JavaScript",
  "TypeScript",
  "C#",
  "C++",
  "Go",
  "PHP",
  "Kotlin",
  "Swift",
  "SQL",
  "MongoDB",
  "PostgreSQL",
  "MySQL",
  "Firebase",
  "Supabase",
  "AWS",
  "Google Cloud",
  "Azure",
  "DevOps",
  "Docker",
  "Kubernetes",
  "AI / Machine Learning",
  "Data Science",
  "Cybersecurity",
  "Blockchain",
  "Other",
];

const EditCodeShop = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [category, setCategory] = useState("React");
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [video, setVideo] = useState(null);

  const [existingThumbnailURL, setExistingThumbnailURL] = useState("");
  const [existingVideoURL, setExistingVideoURL] = useState("");
  const [existingFileURL, setExistingFileURL] = useState("");

  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(db, "codeshop", id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setTitle(data.title);
          setDesc(data.description);
          setPrice(data.price);
          setDiscount(data.discount);
          setCategory(data.category);
          setExistingThumbnailURL(data.thumbnailURL || "");
          setExistingVideoURL(data.videoURL || "");
          setExistingFileURL(data.fileURL || "");
        } else {
          alert("⚠️ Code not found!");
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching document:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleUpdate = async () => {
    if (
      !title.trim() ||
      !desc ||
      desc.trim() === "" ||
      desc === "<p>&nbsp;</p>"
    ) {
      alert("⚠️ Title and description are required");
      return;
    }

    try {
      const docRef = doc(db, "codeshop", id);
      let fileURL = existingFileURL;
      let thumbnailURL = existingThumbnailURL;
      let videoURL = existingVideoURL;

      // Upload new file if provided
      if (file) {
        const fileRef = ref(storage, `codeshop/${Date.now()}-${file.name}`);
        await uploadBytes(fileRef, file);
        fileURL = await getDownloadURL(fileRef);
      }

      // Upload new thumbnail if provided
      if (thumbnail) {
        const thumbRef = ref(
          storage,
          `codeshop-thumbnails/${Date.now()}-${thumbnail.name}`
        );
        await uploadBytes(thumbRef, thumbnail);
        thumbnailURL = await getDownloadURL(thumbRef);
      }

      // Upload new video if provided
      if (video) {
        const videoRef = ref(
          storage,
          `codeshop-videos/${Date.now()}-${video.name}`
        );
        await uploadBytes(videoRef, video);
        videoURL = await getDownloadURL(videoRef);
      }

      const finalPrice = price - (price * discount) / 100;

      await updateDoc(docRef, {
        title,
        description: desc,
        price: Number(price),
        discount: Number(discount),
        finalPrice,
        category,
        fileURL,
        thumbnailURL,
        videoURL,
        author: user?.name || "Admin",
        updatedAt: serverTimestamp(),
      });

      alert("✅ Code updated successfully!");
      navigate("/admin-panel");
    } catch (error) {
      console.error(error);
      alert("❌ Error updating code");
    }
  };

  if (loading)
    return (
      <Wrapper>
        <p>Loading...</p>
      </Wrapper>
    );

  return (
    <Wrapper>
      <Heading>Edit Code Item</Heading>

      <Label>Title</Label>
      <Input
        type="text"
        placeholder="Code Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <Label>Category</Label>
      <Select value={category} onChange={(e) => setCategory(e.target.value)}>
        {technologyCategories.map((tech) => (
          <option key={tech} value={tech}>
            {tech}
          </option>
        ))}
      </Select>

      <Label>Price</Label>
      <Input
        type="number"
        placeholder="Price"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
      />

      <Label>Discount (%)</Label>
      <Input
        type="number"
        placeholder="Discount %"
        value={discount}
        onChange={(e) => setDiscount(e.target.value)}
      />

      <Label>Description</Label>
      <EditorWrapper>
        <CKEditor
          editor={ClassicEditor}
          data={desc}
          onChange={(event, editor) => setDesc(editor.getData())}
        />
      </EditorWrapper>

      <Label>Current Thumbnail</Label>
      {existingThumbnailURL && (
        <ThumbnailPreview src={existingThumbnailURL} alt="Thumbnail" />
      )}
      <Label>Change Thumbnail (optional)</Label>
      {thumbnail && (
        <ThumbnailPreview src={URL.createObjectURL(thumbnail)} alt="Preview" />
      )}
      <Input
        type="file"
        accept="image/*"
        onChange={(e) => setThumbnail(e.target.files[0])}
      />

      <Label>Current Video (optional)</Label>
      {existingVideoURL && <VideoPreview controls src={existingVideoURL} />}
      <Label>Change Video (optional)</Label>
      {video && <VideoPreview controls src={URL.createObjectURL(video)} />}
      <Input
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/mov"
        onChange={(e) => setVideo(e.target.files[0])}
      />

      <Label>Current File</Label>
      {existingFileURL && (
        <p>
          <a href={existingFileURL} target="_blank" rel="noreferrer">
            View current file
          </a>
        </p>
      )}
      <Label>Change File (optional)</Label>
      <Input
        type="file"
        accept=".zip,.rar,.7z,.pdf"
        onChange={(e) => setFile(e.target.files[0])}
      />

      <Button onClick={handleUpdate}>Update</Button>
    </Wrapper>
  );
};

export default EditCodeShop;
